// import React from "react";
// import { Container, Row, Col } from "react-bootstrap";
// import { Typography, Card } from "@mui/material";
// import CheckCircleIcon from "@mui/icons-material/CheckCircle";
// import BrushIcon from "@mui/icons-material/Brush";
// import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
// import { motion } from "framer-motion";
// import { useInView } from "react-intersection-observer";
// import "./style.css";

// interface BenefitItem {
//   title: string;
//   icon: React.ReactElement;
//   desc: string;
// }

// // Slide In from Left Animation
// const slideInLeftVariants = {
//   hidden: { opacity: 0, x: -100 },
//   visible: { opacity: 1, x: 0, transition: { duration: 0.8 } },
// };

// const SnapToonBenefits: React.FC = () => {
//   const { ref: refWhySnapToon, inView: inViewWhySnapToon } = useInView({
//     triggerOnce: true,
//     threshold: 0.2,
//   });

//   const benefits: BenefitItem[] = [
//     {
//       title: "Easy to Use",
//       icon: <CheckCircleIcon />,
//       desc: "No technical skills required! Just upload a photo, pick a face, and let our AI do the magic. Whether you’re a beginner or a pro, our intuitive interface makes face swapping effortless.",
//     },
//     {
//       title: "Realistic Face Swap Technology",
//       icon: <BrushIcon />,
//       desc: "Experience top-notch AI-powered image processing that ensures ultra-realistic blending. Our technology carefully matches skin tones, lighting, and facial expressions for a natural transformation.",
//     },
//     {
//       title: "Safe & Secure",
//       icon: <VerifiedUserIcon />,
//       desc: "Your privacy matters. We never store your photos, and all processing happens securely. Your data is protected with advanced encryption and security measures.",
//     },
//   ];

//   return (
//     <Container className="text-center">
//       <motion.div
//         ref={refWhySnapToon}
//         initial="hidden"
//         animate={inViewWhySnapToon ? "visible" : "hidden"}
//         variants={slideInLeftVariants}
//       >
//         <h2 className="mt-5 mb-4">
//           Why Do You Need SnapToon Face Swap Generator?
//         </h2>
//         <Row className="justify-content-center" style={{ minHeight: 240 }}>
//           {benefits.map((item, index) => (
//             <Col lg={4} key={index} className="mb-4">
//               <motion.div variants={slideInLeftVariants}>
//                 <Card
//                   className="p-4 shadow border-0 text-center feature-card"
//                   style={{ minHeight: 230 }}
//                 >
//                   <div className="feature-icon">{item.icon}</div>
//                   <Typography variant="h6" className="mb-2">
//                     {item.title}
//                   </Typography>
//                   <Typography variant="body2" color="textSecondary">
//                     {item.desc}
//                   </Typography>
//                 </Card>
//               </motion.div>
//             </Col>
//           ))}
//         </Row>
//       </motion.div>
//     </Container>
//   );
// };

// export default SnapToonBenefits;

import React from "react";
import { Container, Row, Col } from "react-bootstrap";
import { Typography, Card } from "@mui/material";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import BrushIcon from "@mui/icons-material/Brush";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import "./style.css";

interface BenefitItem {
  title: string;
  icon: React.ReactElement;
  desc: string;
}

interface FaceSwapBenefitsProps {
  title: string;
  benefits: BenefitItem[];
}

// Slide In from Left Animation
const slideInLeftVariants = {
  hidden: { opacity: 0, x: -100 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.8 } },
};

const FaceSwapBenefits: React.FC<FaceSwapBenefitsProps> = ({
  title,
  benefits,
}) => {
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.2,
  });

  return (
    <Container className="text-center my-5">
      <motion.div
        ref={ref}
        initial="hidden"
        animate={inView ? "visible" : "hidden"}
        variants={slideInLeftVariants}
      >
        <h2 className="gradient-text mb-4" style={{ fontSize: "2.2rem", fontWeight: 800 }}>{title}</h2>
        <Row className="justify-content-center" style={{ minHeight: 240 }}>
          {benefits.map((item, index) => (
            <Col lg={4} key={index} className="mb-4">
              <motion.div variants={slideInLeftVariants} style={{ height: "100%" }}>
                <Card
                  className="glass-card p-4 text-center d-flex flex-column align-items-center justify-content-center"
                  style={{
                    height: "100%",
                    minHeight: 240,
                    background: "rgba(30, 41, 59, 0.6)",
                    backdropFilter: "blur(12px)",
                    border: "1px solid rgba(255, 255, 255, 0.12)",
                    borderRadius: "20px",
                  }}
                >
                  <div className="mb-1" style={{ color: "#c084fc", fontSize: "2rem" }}>{item.icon}</div>
                  <Typography variant="h6" sx={{ color: "#f8fafc", fontWeight: 700, marginBottom: "10px" }}>
                    {item.title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#94a3b8", fontSize: "0.95rem", lineHeight: 1.6 }}>
                    {item.desc}
                  </Typography>
                </Card>
              </motion.div>
            </Col>
          ))}
        </Row>
      </motion.div>
    </Container>
  );
};

const singleFaceSwapBenefits = [
  {
    title: "Easy to Use",
    icon: <CheckCircleIcon />,
    desc: "No technical skills required! Just upload a photo, pick a face, and let our AI do the magic. Whether you’re a beginner or a pro, our intuitive interface makes face swapping effortless.",
  },
  {
    title: "Realistic Face Swap Technology",
    icon: <BrushIcon />,
    desc: "Experience top-notch AI-powered image processing that ensures ultra-realistic blending. Our technology carefully matches skin tones, lighting, and facial expressions for a natural transformation.",
  },
  {
    title: "Safe & Secure",
    icon: <VerifiedUserIcon />,
    desc: "Your privacy matters. We never store your photos, and all processing happens securely. Your data is protected with advanced encryption and security measures.",
  },
];
// Multi-Face Swap Benefits
const multiFaceSwapBenefits = [
  {
    title: "AI-Powered Face Detection",
    icon: <CheckCircleIcon />,
    desc: "SwapToon’s advanced AI detects and swaps multiple faces in one photo with accuracy, ensuring seamless, natural-looking results.",
  },
  {
    title: "Supports All Group Photos",
    icon: <BrushIcon />,
    desc: "Works on family portraits, couple pictures, friend selfies, and group gatherings—no manual editing required!",
  },
  {
    title: "Fun & Creative Possibilities",
    icon: <VerifiedUserIcon />,
    desc: "From realistic transformations to hilarious pranks, SwapToon is perfect for entertainment, social media content, or just having fun with friends.",
  },
];

// Using the reusable component
export const SingleFaceSwapBenefits: React.FC = () => (
  <FaceSwapBenefits
    title="Why Do You Need SwapToon Face Swap Generator?"
    benefits={singleFaceSwapBenefits}
  />
);
// Using the reusable component
export const MultiFaceSwapBenefits: React.FC = () => (
  <FaceSwapBenefits
    title="Why Choose SwapToon for Multi-Face Swapping?"
    benefits={multiFaceSwapBenefits}
  />
);

const preweddingBenefits = [
  {
    title: "Dual Face AI Precision",
    icon: <CheckCircleIcon />,
    desc: "Specially calibrated AI detects both bride and groom faces in couple photos for flawless, high-precision swapping.",
  },
  {
    title: "Realistic Lighting & Skin Blending",
    icon: <BrushIcon />,
    desc: "Ensures natural skin tones, shadow matching, and realistic lighting alignment for studio-grade pre-wedding photos.",
  },
  {
    title: "Instant Romantic Transformations",
    icon: <VerifiedUserIcon />,
    desc: "Transform simple couple selfies into dream destination pre-wedding portraits and royal wedding concepts in seconds.",
  },
];

export const PreweddingBenefits: React.FC = () => (
  <FaceSwapBenefits
    title="Why Choose SwapToon for Pre-Wedding Face Swapping?"
    benefits={preweddingBenefits}
  />
);

const babyshowerBenefits = [
  {
    title: "Multi-Person Detection",
    icon: <CheckCircleIcon />,
    desc: "Seamlessly identifies parents-to-be and family members in baby shower photo templates.",
  },
  {
    title: "Heartwarming & Natural Blends",
    icon: <BrushIcon />,
    desc: "Creates glowing, realistic skin tone blending and expression matching for maternity portraits.",
  },
  {
    title: "Instant Celebration Keepsakes",
    icon: <VerifiedUserIcon />,
    desc: "Generate adorable baby shower, gender reveal, and maternity photos in seconds without costly studio bookings.",
  },
];

export const BabyshowerBenefits: React.FC = () => (
  <FaceSwapBenefits
    title="Why Choose SwapToon for Baby Shower Face Swapping?"
    benefits={babyshowerBenefits}
  />
);
